import * as S from "./styled";
import { EqualHeightElement } from "react-equal-height";
import React, { useState } from "react";
import { Modificators } from "./components";
import { Price } from "~components/Price";
import { Button } from "~common/ui-components/Button/Button";
import { IGetWishlistRes, IProduct } from "@layerok/emojisushi-js-sdk";
import {
  AnimatedTooltip,
  Counter,
  HeartSvg,
  InfoSvg,
  InfoTooltip,
  LogoSvg,
  SkeletonWrap,
  SvgIcon,
} from "~components";
import { useTranslation } from "react-i18next";
import { ReactComponent as ShoppingBag } from "src/assets/ui-icons/shopping-bag.svg";
import { StartAdornment } from "~common/ui-components/Button/StartAdornment";
import { ModalIDEnum } from "~common/modal.constants";
import { useShowModal } from "~modal";
import Skeleton from "react-loading-skeleton";
import { useAddToWishlist } from "~hooks/use-add-to-wishlist";
import { useTheme } from "styled-components";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PRODUCT_ID_SEARCH_QUERY_PARAM } from "~domains/product/products.query";

import { IngredientsTooltipContent } from "~components/ProductCard/components/IngredientsTooltipContent";
import {
  getNewProductPrice,
  getOldProductPrice,
  getProductIngredients,
  getProductMainImage,
  getProductModGroups,
  isProductInWishlists,
} from "~domains/product/product.utils";

import { useRemoveItemFromCart } from "~domains/cart/hooks/use-remove-item-from-cart";
import { useAddProductToCart } from "~domains/cart/hooks/use-add-product-to-cart";
import { Cart } from "~domains/cart/cart.query";
import { SpicySvg } from "~components/svg/SpicySvg";
import { SlightlySpicySvg } from "~components/svg/SlightlySpicySvg";

type ProductCardProps = {
  product?: IProduct;
  loading?: boolean;
  cart?: Cart;
  wishlists?: IGetWishlistRes;
};

export const ProductCard = (props: ProductCardProps) => {
  const { product, loading = false, cart, wishlists } = props;

  const theme = useTheme();
  const showModal = useShowModal();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const cartItems = cart?.items || [];

  const initialModificatorsState =
    product &&
    getProductModGroups(product).reduce(
      (acc, group) => ({
        ...acc,
        [group.property.id]: +group.property.options[0].poster_id,
      }),
      {}
    );

  const [modificators, setModificators] = useState(initialModificatorsState);

  const getVariant = (product: IProduct) => {
    return product?.variants.find((variant) => {
      return !!Object.values(modificators).includes(variant.poster_id); // todo: poster_id exists?
    });
  };

  const variant = getVariant(product);

  const cartItem = product
    ? cartItems.find((item) => item.product.id === product.id)
    : undefined;

  const count = cartItem?.quantity || 0;

  const { mutate: addProductToCart } = useAddProductToCart();

  const { mutate: removeProductFromCart } = useRemoveItemFromCart();

  const favorite = product && isProductInWishlists(product, wishlists || []);

  const oldPrice =
    product && getOldProductPrice(product, variant)?.price_formatted;
  const newPrice =
    product && getNewProductPrice(product, variant)?.price_formatted;

  const { mutate: addToWishlist } = useAddToWishlist();

  const [searchParams] = useSearchParams();

  const openDetailedProductModal = () => {
    showModal(ModalIDEnum.ProductModal);
    searchParams.set(PRODUCT_ID_SEARCH_QUERY_PARAM, product.id + "");
    navigate(
      { search: searchParams.toString() },
      {
        preventScrollReset: true,
      }
    );
  };
  const handleFavouriteButtonClick = () => {
    addToWishlist({
      product_id: product.id,
      quantity: count,
    });
  };

  const mainImage = product && getProductMainImage(product);

  return (
    <S.Wrapper>
      <S.FavouriteButtonWrapper>
        <SkeletonWrap borderRadius="100%" loading={loading}>
          <S.FavouriteButton onClick={handleFavouriteButtonClick}>
            <SvgIcon
              clickable={true}
              width={"100%"}
              color={favorite ? theme.colors.brand : "white"}
              hoverColor={theme.colors.brand}
            >
              <HeartSvg />
            </SvgIcon>
          </S.FavouriteButton>
        </SkeletonWrap>
      </S.FavouriteButtonWrapper>
      <SkeletonWrap loading={loading}>
        <S.Image onClick={openDetailedProductModal} src={mainImage}>
          {!mainImage && (
            <SvgIcon color={"white"} width={"80%"} style={{ opacity: 0.05 }}>
              <LogoSvg />
            </SvgIcon>
          )}
        </S.Image>
      </SkeletonWrap>
      <EqualHeightElement name={"product-name"}>
        <S.Name onClick={openDetailedProductModal}>
          {loading ? (
            <Skeleton />
          ) : (
            <>
              {product.name}
              {!!product?.spicy && (
                <span
                  style={{
                    display: "inline-flex",
                    marginLeft: "4px",
                    verticalAlign: "middle",
                    cursor: "pointer",
                  }}
                >
                  <SvgIcon width="17px" style={{ cursor: "pointer" }}>
                    <SpicySvg />
                  </SvgIcon>
                </span>
              )}
              {!!product?.slightly_spicy && (
                <span
                  style={{
                    display: "inline-flex",
                    marginLeft: "4px",
                    verticalAlign: "middle",
                    cursor: "pointer",
                  }}
                >
                  <SvgIcon width="17px" style={{ cursor: "pointer" }}>
                    <SlightlySpicySvg />
                  </SvgIcon>
                </span>
              )}
            </>
          )}
        </S.Name>
        <Modificators
          loading={loading}
          product={product}
          modificators={modificators}
          setModificators={setModificators}
        />
      </EqualHeightElement>
      <EqualHeightElement name={"description"}>
        <S.Description>
          <SkeletonWrap loading={loading}>
            <InfoTooltip label={t("menu.weightComment")}>
              <S.Weight style={{ alignItems: "center" }}>
                {product?.weight !== 0 ? product?.weight + " г" : ""}
                {product?.weight !== 0 && (
                  <SvgIcon
                    width="20px"
                    color={"#999"}
                    style={{ marginLeft: "2px", cursor: "pointer" }}
                  >
                    <InfoSvg />
                  </SvgIcon>
                )}
              </S.Weight>
            </InfoTooltip>
          </SkeletonWrap>
        </S.Description>
      </EqualHeightElement>
      <EqualHeightElement name={"ingredients"}>
        <S.Description>
          <SkeletonWrap borderRadius="100%" loading={loading}>
            <AnimatedTooltip
              placement={"bottom-start"}
              label={
                <IngredientsTooltipContent
                  items={(product && getProductIngredients(product)) || []}
                  nutrition={
                    product?.calories
                      ? t("menu.nutrition", {
                          calories: Math.floor(product.calories),
                          proteins: Math.floor(product.proteins),
                          fats: Math.floor(product.fats),
                          carbs: Math.floor(product.carbs),
                        })
                      : undefined
                  }
                />
              }
            >
              <Button
                style={{
                  height: "30px",
                  minWidth: "50px",
                  padding: "0 10px",
                  fontSize: "12px",
                }}
              >
                {t("menu.ingredients")}
              </Button>
            </AnimatedTooltip>
          </SkeletonWrap>
        </S.Description>
      </EqualHeightElement>
      <S.Footer>
        <Price loading={loading} oldPrice={oldPrice} newPrice={newPrice} />
        {count ? (
          <Counter
            handleIncrement={() => {
              addProductToCart({
                quantity: count + 1,
                product: product,
              });
            }}
            handleDecrement={() => {
              const nextCount = count - 1;
              if (nextCount < 1) {
                removeProductFromCart({
                  id: cartItem.id,
                });
              } else {
                addProductToCart({
                  quantity: nextCount,
                  product: product,
                });
              }
            }}
            count={count}
          />
        ) : (
          <Button
            style={{
              width: 130,
            }}
            startAdornment={
              <StartAdornment>
                <ShoppingBag />
              </StartAdornment>
            }
            showSkeleton={loading}
            onClick={() => {
              addProductToCart({
                quantity: 1,
                product: product,
              });
            }}
          >
            {t("order.order_btn")}
          </Button>
        )}
      </S.Footer>
    </S.Wrapper>
  );
};
